import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';
import { ART_STYLE } from '@/nova/theme/tokens';

import type { ArtStyleMascot } from '../../../art-style/art-style-api';
import { MascotAvatar } from '../../../art-style/components/MascotAvatar';
import { LANDING_CAST_MAX, LANDING_CAST_NONE, isCastNone } from '../../landing-page-api';
import * as S from './LandingPageComposer.styles';

export interface SceneCastProps {
  /** The art style's cast a scene can feature (its mascots with an image, see `adCast`). */
  mascots: ArtStyleMascot[];
  /** The page's cast: mascot ids (left, right), `['none']`, or [] for automatic. */
  value: string[];
  /** What the characters flank: a full-screen game's game (the default), or the regular page's offer. */
  around?: 'game' | 'offer';
  disabled: boolean;
  onChange: (next: string[]) => void;
}

/** The picked mascots the cast still has, in order (an id the cast no longer has is dropped, as the service does).
 *  Pure. */
export const pickedCast = (value: readonly string[], mascots: readonly ArtStyleMascot[]): string[] =>
  isCastNone(value) ? [] : value.filter((id) => mascots.some((m) => m.id === id));

/** The cast after a press on a mascot: a picked one leaves (the other moves to the left), another joins on the right
 *  while there is room; nothing picked any more is automatic. Pure. */
export const toggleCastMember = (picked: readonly string[], id: string): string[] => {
  if (picked.includes(id)) return picked.filter((x) => x !== id);
  if (picked.length >= LANDING_CAST_MAX) return [...picked];
  return [...picked, id];
};

const firstImage = (mascot: ArtStyleMascot): string | undefined => mascot.images[0]?.url;

/**
 * Which of the art style's characters a scene shows: Automatic (the default: Lumio features its cast), No characters,
 * or up to two picked mascots, the first left of the game (the regular page: of the offer) and the second right (Swap
 * sides flips them). A changed pick is drawn by the next Generate with Scene art on; No characters shows at once.
 */
export const SceneCast = ({
  mascots,
  value,
  around = 'game',
  disabled,
  onChange,
}: SceneCastProps): React.ReactElement => {
  const { t } = useTranslation();
  const none = isCastNone(value);
  const picked = pickedCast(value, mascots);
  const auto = !none && picked.length === 0;
  const full = picked.length >= LANDING_CAST_MAX;
  const nameOf = (mascot: ArtStyleMascot): string => mascot.name.trim() || t('Unnamed character');
  const set = (next: string[]): void => {
    if (!disabled) onChange(next);
  };

  return (
    <S.Stack>
      <S.GroupHead>
        <S.GroupLabel text="Characters" />
        {picked.length === 2 ? (
          <Button
            variant="text"
            label={t('Swap sides')}
            disabled={disabled}
            onClick={() => set([picked[1], picked[0]])}
          />
        ) : null}
      </S.GroupHead>
      <S.CastModes>
        <S.CastMode type="button" aria-pressed={auto} $selected={auto} disabled={disabled} onClick={() => set([])}>
          <MascotAvatar url={mascots[0] ? firstImage(mascots[0]) : undefined} size={ART_STYLE.MASCOT_MENU_AVATAR_PX} />
          <S.OfferName text="Automatic" />
        </S.CastMode>
        <S.CastMode
          type="button"
          aria-pressed={none}
          $selected={none}
          disabled={disabled}
          onClick={() => set([LANDING_CAST_NONE])}
        >
          <MascotAvatar size={ART_STYLE.MASCOT_MENU_AVATAR_PX} muted />
          <S.OfferName text="No characters" />
        </S.CastMode>
      </S.CastModes>
      <S.CastGrid role="group" aria-label={t('Your characters')}>
        {mascots.map((mascot) => {
          const index = picked.indexOf(mascot.id);
          const selected = index >= 0;
          const url = firstImage(mascot);
          return (
            <S.CastCard
              key={mascot.id}
              type="button"
              aria-pressed={selected}
              $selected={selected}
              disabled={disabled || (full && !selected)}
              title={full && !selected ? t('Two characters are picked. Remove one to pick another.') : undefined}
              onClick={() => set(toggleCastMember(picked, mascot.id))}
            >
              {url ? <S.SceneThumb src={url} alt="" /> : null}
              <S.CastName raw text={nameOf(mascot)} />
              {selected ? <S.CastSide text={index === 0 ? 'Left' : 'Right'} /> : null}
            </S.CastCard>
          );
        })}
      </S.CastGrid>
      {around === 'offer' ? (
        none ? (
          <S.Hint text="The page shows no characters." />
        ) : auto ? (
          <S.Hint text="Lumio features up to two of your characters. Pick them yourself: the first stands left of the offer, the second right." />
        ) : (
          <S.Hint text="The first stands left of the offer, the second right. Generate with Scene art on to draw them." />
        )
      ) : none ? (
        <S.Hint text="The game shows no characters." />
      ) : auto ? (
        <S.Hint text="Lumio features up to two of your characters. Pick them yourself: the first stands left of the game, the second right." />
      ) : (
        <S.Hint text="The first stands left of the game, the second right. Generate with Scene art on to draw them." />
      )}
    </S.Stack>
  );
};

export default SceneCast;
